/**
 * Notification helpers: deep-link routing and time formatting.
 */

import authService from '../services/authService';

const TIME_ZONE = 'Europe/Belgrade';

/**
 * Resolve the in-app route for a notification based on its related entity.
 * @param {Object} notification - NotificationDto
 * @returns {string|null} Route path or null when there is no deep-link target
 */
export const routeForNotification = (notification) => {
  if (!notification || !notification.entityId) return null;

  switch (notification.entityType) {
    case 'Task':
    case 'TaskNote': {
      // Caregivers, physiotherapists and nurses use their own tasks pages;
      // other roles use the admin page.
      const role = authService.getUserRole();
      const base =
        role === 'caregiver'
          ? '/caregiver-tasks'
          : role === 'physiotherapist'
          ? '/physiotherapist-tasks'
          : role === 'nurse' || role === 'caretaker'
          ? '/nurse-tasks'
          : '/tasks';
      return `${base}/${notification.entityId}`;
    }
    case 'Shift':
      return '/shift-management';
    case 'Resident':
    case 'ResidentNote':
      return `/residents/${notification.entityId}`;
    // ResidentDocument (enum value 7): entityId is the resident id, so open the
    // resident profile directly on the "Dokumenti" tab.
    case 'ResidentDocument':
    case 7:
      return `/residents/${notification.entityId}?tab=documents`;
    case 'ProblemReport':
      return '/tasks';
    default:
      return null;
  }
};

/**
 * Format a notification timestamp as a relative, human-friendly string
 * (e.g. "Pre 5 minuta", "Pre 2 dana"), falling back to an absolute date
 * for anything older than a week.
 * @param {string} iso - ISO 8601 timestamp (UTC)
 * @returns {string} Formatted time
 */
export const formatNotificationTime = (iso) => {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Upravo sada';
  if (diffMinutes < 60) {
    return `Pre ${diffMinutes} ${diffMinutes === 1 ? 'minut' : 'minuta'}`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `Pre ${diffHours} ${diffHours === 1 ? 'sat' : 'sata'}`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Juče';
  if (diffDays < 7) return `Pre ${diffDays} dana`;

  return new Intl.DateTimeFormat('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TIME_ZONE,
  }).format(date);
};

/**
 * Map a notification type to a Carbon toast kind.
 * @param {string} type - NotificationType
 * @returns {'success'|'error'|'warning'|'info'} Toast kind
 */
export const toastKindForType = (type) => {
  switch (type) {
    case 'TaskAssigned':
    case 'ShiftAssigned':
    case 'TaskCompleted':
      return 'success';
    case 'TaskCanceled':
    case 'ShiftCanceled':
    case 'ProblemReported':
      return 'error';
    case 'ShiftChanged':
      return 'warning';
    default:
      return 'info';
  }
};
